import { useState } from "react";
import { FaCodeBranch, FaCodeMerge, FaKey } from "react-icons/fa6";
import "./user-profile-sidemodal.style.scss";

import { useFormik } from "formik";
import * as yup from "yup";

// components
import DialogModal from "../dialog-modal/dialog-modal.component";
import { Button } from "../ui/button";
import TextField from "../form-inputs/text-field.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";

// global functions and hooks
import { queryClient } from "../../global/query-client";
import { useDebounce } from "../../hooks/useDebounce";
import { useSession } from "../../queries/auth.queries";
import {
	EmployeePayload,
	EmployeePermissions,
	UsernameConflictError,
	buildProfileSnapshotPayload,
	employeesQueryKey,
	useUpdateEmployee,
	useEmployeeDetails,
	useCheckUsername,
} from "../../queries/employee.queries";

interface EmployeeLoginManagerProps {
	userInfoId: string;
	empPermission?: EmployeePermissions;
	currentPageNum?: number;
}

interface LoginFormValues {
	username: string;
	password: string;
}

// Split out of employee-profile.component.tsx: login/credential changes carry
// a materially different blast radius than editing a name or phone number —
// a shared login spans every company that user belongs to — so this owns its
// own edit surface, validation, and mutation call, entirely independent of
// the personal-fields form (see the profile-review discussion this was split
// from). Self-contained: given just a userInfoId, it fetches its own
// useEmployeeDetails (React Query dedupes this against the profile's own call
// to the same key) and renders both the read-only summary and the "Manage"
// dialog.
function EmployeeLoginManager({
	userInfoId,
	empPermission,
	currentPageNum,
}: EmployeeLoginManagerProps) {
	const { data: session } = useSession();
	const { data: employeeDetails } = useEmployeeDetails(userInfoId);

	const [manageOpen, setManageOpen] = useState(false);
	// Only meaningful while this company shares its login with others — the
	// user has opted to give this company its own separate credentials
	// instead of editing the shared login directly.
	const [splitMode, setSplitMode] = useState(false);
	const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
	// The username this company was already using when the dialog opened.
	// checkUsername's "is this already mine" check only compares against a
	// split login's own login_info_id — for a non-split (shared) login,
	// company_users.login_info_id is null and can never equal a real login's
	// id, so re-checking an unchanged username would always come back as a
	// false "own_shared_login" conflict. Skipping the check when the value
	// hasn't actually changed avoids that false positive entirely.
	const [originalUsername, setOriginalUsername] = useState("");

	const updateLoginMutation = useUpdateEmployee();

	// Structured username-conflict response (own_shared_login/own_split_login)
	// — when merge_available, offer a merge action instead of a dead-end error.
	const conflictError =
		updateLoginMutation.error instanceof UsernameConflictError
			? updateLoginMutation.error
			: null;

	const loginShared = employeeDetails?.login_shared;
	const otherCompanyCount = employeeDetails?.other_company_count ?? 0;
	const isLoginSplit = employeeDetails?.is_login_split;
	const sharedLogin = employeeDetails?.shared_login;
	// Best-effort current username: this company's split login if one exists,
	// else the user's oldest known login — same derivation the profile's own
	// prefill uses for the same reason (getUserById doesn't hand back a single
	// canonical "current username" field).
	const currentUsername =
		employeeDetails?.company_login?.username ??
		employeeDetails?.user?.logins?.[0]?.username ??
		"";

	const loginSchema = yup.object().shape({
		username: splitMode
			? yup
					.string()
					.min(3, "Username must be at least 3 characters")
					.required("Username is required to create separate credentials")
			: yup.string().min(3, "Username must be at least 3 characters"),
		password: splitMode
			? yup
					.string()
					.min(8, "Password must be at least 8 characters")
					.required("Password is required to create separate credentials")
			: yup.string().min(8, "Password must be at least 8 characters"),
	});

	const formik = useFormik<LoginFormValues>({
		validationSchema: loginSchema,
		initialValues: { username: "", password: "" },
		onSubmit: async (values) => {
			const payload = buildDefensivePayload();
			if (!payload) return;

			if (splitMode) {
				payload.split_login = true;
				payload.username = values.username;
				payload.password = values.password;
			} else {
				// Not splitting — only forward username/password if the user
				// actually typed something (blank means "leave login alone").
				if (values.username) payload.username = values.username;
				if (values.password) payload.password = values.password;
			}

			try {
				await updateLoginMutation.mutateAsync({
					employeeData: payload,
					page: currentPageNum,
				});
			} catch {
				return; // FormError below renders the message
			}

			afterLoginMutationSuccess();
		},
	});

	// See buildProfileSnapshotPayload — updateUser has no undefined-filtering,
	// so every login-only submit still has to round-trip the current personal-
	// field snapshot, same defensive spread the old inline handleMergeConfirm
	// used to do off formik.values before this was split out.
	const buildDefensivePayload = (): EmployeePayload | null => {
		const currentUser = employeeDetails?.user;
		if (!currentUser) return null;
		return buildProfileSnapshotPayload(currentUser);
	};

	const afterLoginMutationSuccess = () => {
		queryClient.invalidateQueries({ queryKey: employeesQueryKey });
		// Split/merge just changed login_shared/is_login_split/shared_login/
		// company_login for this user — without this, the summary below shows
		// stale state until the modal is closed and reopened.
		queryClient.invalidateQueries({ queryKey: ["employeeDetails", userInfoId] });
		if (userInfoId === session?.user_info_id) {
			queryClient.invalidateQueries({ queryKey: ["session"] });
		}
		handleManageOpenChange(false);
	};

	// Merges this company's split login back into the shared one. Bypasses
	// formik's own onSubmit/validation (an unrelated action from the
	// username/password fields) but still carries the defensive personal-field
	// snapshot along, same reasoning as buildDefensivePayload above.
	const handleMergeConfirm = async () => {
		setMergeConfirmOpen(false);
		const payload = buildDefensivePayload();
		if (!payload) return;
		payload.merge_login = true;

		try {
			await updateLoginMutation.mutateAsync({
				employeeData: payload,
				page: currentPageNum,
			});
		} catch {
			return; // FormError below renders the message
		}

		afterLoginMutationSuccess();
	};

	const handleOpenManage = () => {
		formik.resetForm({ values: { username: currentUsername, password: "" } });
		setOriginalUsername(currentUsername);
		setSplitMode(false);
		updateLoginMutation.reset();
		setManageOpen(true);
	};

	const handleManageOpenChange = (open: boolean) => {
		if (!open) {
			formik.resetForm();
			setSplitMode(false);
			updateLoginMutation.reset();
		}
		setManageOpen(open);
	};

	const debouncedUsername = useDebounce(formik.values.username, 400);
	const usernameToCheck =
		debouncedUsername.trim() !== originalUsername.trim() ? debouncedUsername : "";
	const { data: usernameCheck, isFetching: isCheckingUsername } =
		useCheckUsername(usernameToCheck, userInfoId);

	const trimmedUsername = formik.values.username.trim();

	return (
		<>
			<div className="user-info-container">
				<div className="section-header-row">
					<h2 className="section-header">Login Details</h2>
					{empPermission?.edit && (
						<Button
							type="button"
							size="sm"
							variant="outline"
							onClick={handleOpenManage}
						>
							<FaKey /> Manage
						</Button>
					)}
				</div>

				{isLoginSplit ? (
					<InlineNotice
						message="This company has its own separate login for this user."
						type="info"
					/>
				) : loginShared ? (
					<InlineNotice
						message={`Shared login — used by ${otherCompanyCount} other ${
							otherCompanyCount === 1 ? "company" : "companies"
						}.`}
						type="warning"
					/>
				) : (
					<InlineNotice
						message="This user's login is not shared with any other company."
						type="info"
					/>
				)}
			</div>

			<DialogModal
				open={manageOpen}
				onOpenChange={handleManageOpenChange}
				title="Manage Login"
				description={
					splitMode
						? "Create a separate username and password used only by this company."
						: "Leave a field blank to keep it unchanged."
				}
				footer={
					<>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleManageOpenChange(false)}
							disabled={updateLoginMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={updateLoginMutation.isPending}
						>
							{updateLoginMutation.isPending ? "Saving..." : "Save"}
						</Button>
					</>
				}
			>
				<div className="user-info-container">
					<FormError
						isError={updateLoginMutation.isError}
						error={updateLoginMutation.error}
						fallback="Something went wrong. Please try again."
					/>

					{conflictError?.merge_available && (
						<div className="login-mode-toggle">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setMergeConfirmOpen(true)}
							>
								<FaCodeMerge />
								Merge back to{" "}
								{conflictError.shared_login
									? `@${conflictError.shared_login.username}`
									: "shared login"}
							</Button>
						</div>
					)}

					{loginShared && !isLoginSplit && (
						<div className="login-mode-toggle">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setSplitMode((prev) => !prev)}
							>
								<FaCodeBranch />
								{splitMode
									? "Cancel separate login"
									: "Use a separate login for this company"}
							</Button>
						</div>
					)}

					{isLoginSplit && sharedLogin && (
						<div className="login-mode-toggle">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setMergeConfirmOpen(true)}
							>
								<FaCodeMerge /> Merge back to @{sharedLogin.username}
							</Button>
						</div>
					)}

					<div className="input-wrapper">
						<label className="input-label">
							{splitMode ? "New Username" : "Username"}
						</label>
						<input
							name="username"
							className="input-box-wrapper"
							type="text"
							placeholder={
								splitMode
									? "Enter a new username for this company"
									: "Leave blank to keep current username"
							}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values.username}
							autoComplete="off"
						/>
						{formik.submitCount > 0 && formik.errors.username ? (
							<div className="validation-error">{formik.errors.username}</div>
						) : (
							trimmedUsername.length >= 3 &&
							trimmedUsername !== originalUsername.trim() &&
							!isCheckingUsername &&
							usernameCheck &&
							(usernameCheck.available === true ? (
								<div className="username-hint success">Username available</div>
							) : usernameCheck.available === false ? (
								<div className="username-hint error">
									{usernameCheck.message || "Username already taken"}
								</div>
							) : null)
						)}
					</div>
					<TextField
						name="password"
						label={
							splitMode
								? "New Password"
								: "New Password (leave blank to keep current)"
						}
						placeholder="Enter login password"
						formik={formik}
						securetext
					/>
				</div>
			</DialogModal>

			<DialogModal
				open={mergeConfirmOpen}
				onOpenChange={setMergeConfirmOpen}
				title="Merge back to shared login?"
				description="This company will stop using its own separate login and go back to sharing the login used by this user's other companies."
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => setMergeConfirmOpen(false)}
							disabled={updateLoginMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleMergeConfirm}
							disabled={updateLoginMutation.isPending}
						>
							{updateLoginMutation.isPending ? "Merging..." : "Merge"}
						</Button>
					</>
				}
			/>
		</>
	);
}

export default EmployeeLoginManager;
